import classNames from 'classnames'
import * as keycodes from 'keycode'
import * as React from 'react'

import './Header.scss'

const scroll = 20

interface HeaderProps {
  type?: string
  logoLink?: JSX.Element
  menuLinks?: JSX.Element
  languageLinks?: JSX.Element
  bookButton?: JSX.Element
  loginComponent?: JSX.Element
  currentCountry?: string
  availableCurrencies?: string[]
  onChangeCurrency?: (value: string) => void
  hideMenu?: boolean
  overrideColour?: 'black' | 'white'
}

interface HeaderState {
  HeaderOpen: boolean
  LocaleOpen: boolean
  ScrollSolid: boolean
}

class Header extends React.Component<HeaderProps, HeaderState> {
  public mobileRef?: any
  public ref?: any
  public overlayRef?: any

  constructor(props: HeaderProps) {
    super(props)
    this.state = {
      HeaderOpen: false,
      LocaleOpen: false,
      ScrollSolid: false,
    }
    this.openHeader = this.openHeader.bind(this)
    this.openLocale = this.openLocale.bind(this)
    this.closeLocale = this.closeLocale.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this)
  }

  public componentDidMount() {
    this.handleScroll()
    window.addEventListener('scroll', this.handleScroll, true)
    window.addEventListener('resize', this.handleScroll, true)
    window.addEventListener('click', this.handleOutsideClick, true)
    window.addEventListener('keydown', this.handleKeyDown, true)
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll, true)
    window.removeEventListener('resize', this.handleScroll, true)
    window.removeEventListener('click', this.handleOutsideClick, true)
    window.removeEventListener('keydown', this.handleKeyDown, true)
  }

  public handleScroll(e?: Event) {
    let scrollTop = 0

    if (
      document.body &&
      (document.body.scrollLeft || document.body.scrollTop)
    ) {
      // DOM compliant
      scrollTop = document.body.scrollTop
    } else if (typeof window.pageYOffset === 'number') {
      // Netscape compliant
      scrollTop = window.pageYOffset
    } else if (
      document.scrollingElement &&
      (document.scrollingElement.scrollLeft ||
        document.scrollingElement.scrollTop)
    ) {
      //
      scrollTop = document.scrollingElement.scrollTop
    } else if (
      document.documentElement &&
      (document.documentElement.scrollLeft ||
        document.documentElement.scrollTop)
    ) {
      // IE6 standards compliant modet
      scrollTop = document.documentElement.scrollTop
    }

    this.setState({
      ScrollSolid: scrollTop > scroll,
    })
  }

  public setRef = (node: HTMLUListElement | null, mobile: boolean = false) => {
    if (mobile) {
      this.mobileRef = node
    } else {
      this.ref = node
    }
  }

  public handleOutsideClick = (e: MouseEvent) => {
    if (
      this.ref !== null &&
      !this.ref.contains(e.target) &&
      !this.overlayRef.contains(e.target) &&
      !this.mobileRef.contains(e.target)
    ) {
      this.closeLocale()
    }
  }

  public openHeader(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault()
    this.setState({
      HeaderOpen: !this.state.HeaderOpen,
    })
  }

  public openLocale(e: React.MouseEvent<HTMLLIElement>) {
    e.preventDefault()
    this.setState({
      LocaleOpen: !this.state.LocaleOpen,
    })
  }

  public closeHeader() {
    this.setState({ HeaderOpen: false })
  }

  public closeLocale() {
    this.setState({ LocaleOpen: false })
  }

  public handleCurrencyChange(value: string) {
    if (this.props.onChangeCurrency) {
      this.props.onChangeCurrency(value)
    }
  }

  public handleKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === keycodes.codes.esc) {
      this.handleClose()
    }
  }

  public handleClose = () => {
    if (this.state.LocaleOpen) {
      this.closeLocale()
    } else {
      this.closeHeader()
    }
  }

  public render() {
    const className = classNames(
      'srprs-header',
      this.props.type ? `is-${this.props.type}` : '',
      this.state.ScrollSolid && this.props.type !== 'funnel' ? 'is-solid' : '',
      this.state.HeaderOpen ? 'is-open' : '',
      this.state.LocaleOpen ? 'is-locked' : '',
      this.props.hideMenu ? 'no-menu' : '',
      this.props.overrideColour ? `override-${this.props.overrideColour}` : '',
    )

    const renderLocale = (mobile: boolean = false) => (
      <div className="locale">
        <ul
          className={`locale-dropdown ${
            this.state.LocaleOpen ? 'is-open' : ''
          }`}
          ref={node => this.setRef(node, mobile)}
        >
          <li className="locale-currentLanguage" onClick={this.openLocale}>
            <span>{this.props.currentCountry}</span>
            <span className="chevron" />
          </li>
          <li className={`locale-line`} />
          {this.props.languageLinks}
        </ul>
      </div>
    )

    const renderHamburger = () => (
      <div className="hamburger-wrapper">
        <div className="hamburger" onClick={this.openHeader}>
          <span className="bar" />
        </div>
      </div>
    )

    return (
      <header className={className} role="navigation">
        <nav className="wrapper-inner">
          <div className="logo">{this.props.logoLink}</div>

          <div className="wrapper-mobile">
            {renderHamburger()}
            <div className="wrapper-menu">
              <div className="wrapper-header">
                {renderLocale(true)}
                <div className="locale-overlay" />
                <div className="login">{this.props.loginComponent}</div>
              </div>
              <div className="wrapper-content">{this.props.menuLinks}</div>
              {this.props.bookButton}
            </div>
          </div>

          <div className="wrapper-desktop">
            <div className="secure-overflow">
              {this.props.menuLinks}
              {renderHamburger()}
            </div>

            {renderLocale()}

            <div className="login">{this.props.loginComponent}</div>
            {this.props.bookButton}
          </div>
          <div
            className="overlay"
            ref={ref => {
              this.overlayRef = ref
            }}
            onClick={this.handleClose}
          ></div>
        </nav>
      </header>
    )
  }
}

export default Header
